import React, {
    useCallback,
    useEffect,
    useState,
    useContext
} from 'react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { ViewModelForm, ViewModelServiceContext, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { PolicyChange } from 'gw-capability-policychange-common-react';
import _ from 'lodash';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { MLModalService, MLTagManagerService } from 'ml-portals-services-react';
import { MLEndorsementFlowUtil, MLEndorsementUtil } from 'ml-portals-util-js';
import { MLNotificationBlockUtil } from 'ml-portals-util-react';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import appConfig from 'app-config';
import { MLPolicyChangeDataHolderService, MLNavigationStepsTrackingService } from 'ml-services-common-react';
import { useModal } from '@jutro/components';
import { FormModalPopover } from 'ml-components-platform-react';
import { WatercraftPopupComponent, UnderlyingPolicyComponent } from 'ml-capability-quoteandbind-common-react';
import addUnderlyingPolicyPopupMetadata from '../../../ml-capability-quoteandbind-common-react/pages/UnderlyingPolicies/AddEditUnderlyingPolicyPopup.metadata.json5';
import metadata from './WatercraftPagePolChange.metadata.json5';

import messages from './WatercraftPagePolChange.messages';
import styles from './WatercraftPagePolChange.module.scss';
import { ScrollToError } from '@jutro/legacy/wizard-next';

const alertNames = {
    addedWatercraftName: 'addedWatercraft'
};

const WatercraftPagePolChange = (props) => {
    const modalApi = useModal();
    const {
        wizardData,
        updateWizardData,
        goNext,
        currentStepIndex,
        steps,
        currentStep,
        changeNextSteps
    } = props;
    const { submissionVM, alertMessages } = wizardData;
    const { EndorsementService } = useDependencies('EndorsementService');
    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useTranslator();
    const { authHeader } = useAuthentication();
    const { refreshData } = useDataRefresh();

    const {
        showProgressDialog
    } = MLModalService();

    const MLAlertService = MLNotificationBlockUtil();
    const isGPA = appConfig.portalCode === 'GPA';
    const isAMP = appConfig.portalCode === 'AMP';
    const pupModel = _.get(submissionVM, 'lobData.personalUmbrella', []);
    const UNDERLYINGPOLICIES_PATH = 'lobData.personalUmbrella.underlyingPolicies';
    const underlyingPoliciesVM = _.get(submissionVM, UNDERLYINGPOLICIES_PATH);
    const isModernProduct = _.get(submissionVM, 'isModernProduct_Ext.value', false);
    const underlyingPoliciesVal = _.get(submissionVM, `${UNDERLYINGPOLICIES_PATH}.value`);
    const productCode = _.get(submissionVM, 'baseData.productCode.value', '');

    const [watercraft, setWatercraft] = useState(viewModelService.create({}, 'pc', 'metlife.edge.capabilities.common.plugin.dto.PUPWatercraftDTOML'));
    watercraft.isModernProduct.value = _.get(submissionVM, 'isModernProduct_Ext.value', undefined);
    const [isInSync, setInSync] = useState(false);
    const [pageAction, setPageAction] = useState('');
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const [errorTimestamp, setErrorTimestamp] = useState(0);

    const { onValidate } = useValidation('WatercraftPagePolChange');

    const updateWizardSubmissionData = useCallback((vm) => {
        _.set(wizardData, 'submissionVM', vm);
        updateWizardData(wizardData);
    }, [updateWizardData, wizardData]);

    const setCurrentWatercraftVM = (lobVM) => {
        const currentWatercraftVMs = _.get(lobVM, 'personalUmbrella.coverables.watercrafts');
        const { additionalData } = currentStep;
        let watercraftVM = viewModelService.create({}, 'pc', 'metlife.edge.capabilities.common.plugin.dto.PUPWatercraftDTOML')
        const selectedWatercraftVM = currentWatercraftVMs.children.find((wc) => wc.value.isSelected === true);
        
         if(additionalData){
            // if Additional Data is availabe
            watercraftVM = currentWatercraftVMs.children.find((wc) => wc.value.fixedID === additionalData.fixedID);
        }
        
        if(!_.isUndefined(selectedWatercraftVM)){
            // if the isSelected is available
            watercraftVM = selectedWatercraftVM
        }


        return watercraftVM;
    };

    const initWatercraftPage = () => {
        setWatercraft(setCurrentWatercraftVM(submissionVM.lobData));

        let pageActionLabel = MLPolicyChangeDataHolderService.getPageAction();

        if (currentStep.pageAction) {
            pageActionLabel = currentStep.pageAction
        }
        
        if (pageActionLabel === 'add') {
            setPageAction('add');
            MLNavigationStepsTrackingService.addToDynamicSteps(translator(messages.addWatercraftDynamic), false, currentStep.mlStepName, authHeader);
        } else if (pageActionLabel === 'edit') {
            setPageAction('edit');
            MLNavigationStepsTrackingService.addToDynamicSteps(translator(messages.editWatercraftDynamic), false, currentStep.mlStepName, authHeader);
        } 
    };

    useEffect(() => {
        if (MLEndorsementFlowUtil.startingPointPageLoadIsSkipping(submissionVM, goNext, steps, currentStepIndex, changeNextSteps)) {
            return;
        };
        initWatercraftPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getNextStepText = () => {
        return (
            <FormattedMessage
                {...messages.nextBaseDescription}
                values={{
                    bold: (
                        <strong>{translator(messages.nextDescriptionBold)}</strong>
                    )
                }}
            />
        );
    };

    const getWatercraftName = (item) => {
        return `${item.value.year} ${item.value.make} ${item.value.model}`;
    }

    const addNewWatercraftAlert = (item) => {
        const watercraftTypeCode = item.watercraftType.value.code;
        const watercraftTypeOption = item.watercraftType.aspects.availableValues.find(
            (type)=> type.code === watercraftTypeCode
        )

        const watercraftTypeName = watercraftTypeOption
        ? translator({id: watercraftTypeOption.name, defaultMessage: watercraftTypeOption.name})    
        : watercraftTypeCode;

        const alertMsg = {
            alertName: alertNames.addedWatercraftName,
            formattedMessageName: 'watercraftXHasBeenAdded',
            messagePram: `${item.year.value} ${watercraftTypeName} `,
        };
        const alertExists = alertMessages.some((msg) => {
            if (_.isEqual(msg, alertMsg)) {
                return true;
            }
            
            return false;
        });

        if (!alertExists) {
            alertMessages.push(alertMsg);
        }

    };

    const getPopupProps = (underlyingVM, isEdit = false) => {
        const componentProps = {
            title: messages.addAPolicy,
            viewModel: underlyingVM,
            uiProps: addUnderlyingPolicyPopupMetadata.pageContent,
            actionBtnLabel: messages.addAPolicy,
            cancelBtnLabel: messages.cancel,
            getOverrideProps: () => {
                return {
                    underlyingPolicyComponent: {
                        underlyingPolicyVM: underlyingVM,
                        quoteID: submissionVM.value.jobID,
                        showValidationErrors,
                        isEdit,
                        authHeader,
                        isComponent: true,
                        isPolicyChange: true,
                        MLAlertService,
                        isModernProduct
                    },
                };
            },
            customComponentResolvers: {
                underlyingPolicyComponent: UnderlyingPolicyComponent
            },
        };

        return componentProps;
    };

    const addUnderlyingPolicy = (ulPolicyVM) => {
        if (!(ulPolicyVM.aspects.valid && ulPolicyVM.aspects.subtreeValid)) {
            setShowValidationErrors(true);
            setErrorTimestamp(errorTimestamp+1);
            return;
        }
        watercraft.ulPolicyFixID.value = ulPolicyVM.ulPolicyFixID.value;
        updateWizardSubmissionData(submissionVM);
        return true;
    };

    const cancelAddUnderlyingPolicy = (policy) => {
        pupModel.value.removeUnderlyingPolicy(policy);
        watercraft.ulPolicyFixID.value = undefined;
        updateWizardSubmissionData(submissionVM);
    };

    const createNewULPolicy = () => {
        const index = underlyingPoliciesVal.findIndex((ulPolicy) => ulPolicy.ulPolicyFixID === 'new');
        const ulPolicy = underlyingPoliciesVM.getElement(index);

        let componentProps = getPopupProps(ulPolicy);
        componentProps = {
            ...componentProps,
            onSaveAction: addUnderlyingPolicy
        };
        modalApi.showModal(
            <FormModalPopover {...componentProps} />
        ).catch(() => cancelAddUnderlyingPolicy(ulPolicy.value));
    };

    const getUnderlyingPolicies = () => {
        if (isInSync === false) {
            if (underlyingPoliciesVal.findIndex((ulPolicy) => ulPolicy.ulPolicyFixID === 'new') === -1) {
                pupModel.value.createUnderlyingPolicy({ 
                    ulPolicyFixID: 'new'
                });
            }
        }

        const mappedValues = underlyingPoliciesVal.map((policy) => {
            let underlyingPolicyDisplayName = policy.getDisplayName(viewModelService.productMetadata, translator);
            if(isModernProduct && policy.policyType === 'HO') {
                underlyingPolicyDisplayName = underlyingPolicyDisplayName.replace('Homeowners', 'Property');
            }
            if (policy.ulPolicyFixID === 'new') {
                return {
                    code: policy.ulPolicyFixID,
                    name: translator(messages.underlyingPolicyNotListed)
                }
            } 
            return {
                code: policy.ulPolicyFixID,
                name: translator({
                    id: policy.policyNumber,
                    defaultMessage: underlyingPolicyDisplayName
                })
            };
        });

        return mappedValues;
    };

    const syncExposure = () => {
        setInSync(true);

        return EndorsementService.saveEndorsement([submissionVM.value], authHeader).then((updatedEndorsementData) => {
            const policyChange = new PolicyChange(Object.assign({}, updatedEndorsementData));
            const updatedEndorsementVM = viewModelService.create(policyChange, 'pc', 'edge.capabilities.policychange.dto.PolicyChangeDataDTO');
            _.set(wizardData, 'submissionVM', updatedEndorsementVM);

            setInSync(false);
            return updatedEndorsementVM;
        }).catch(() => {
            setInSync(false);
            return submissionVM;
        });
    };

    const syncWatercraftExposure = () => {
        setFixIdsOfNonEditingWatercraft();
        return syncExposure().then((updatedEndorsementVM) => setCurrentWatercraftVM(updatedEndorsementVM.lobData));
    };

    const jumpToSelectedStepByStepID = (newStepID) => {
        MLEndorsementFlowUtil.addAndNavigateToSelectedStep(newStepID, steps, currentStepIndex, changeNextSteps, goNext, productCode); 
    };

    const saveEndorsement = () => {
        const promise = EndorsementService.saveEndorsement([submissionVM.value], authHeader);
        showProgressDialog(messages.saving, promise);
        promise.then((updatedPolicyChange) => {
            const policyChange = new PolicyChange(Object.assign({}, updatedPolicyChange));
            const updatedEndorsementVM = viewModelService.create(policyChange, 'pc', 'edge.capabilities.policychange.dto.PolicyChangeDataDTO');
            
            updateWizardSubmissionData(updatedEndorsementVM);

            const Watercrafts = _.get(updatedEndorsementVM, 'value.lobData.personalUmbrella.coverables.watercrafts');
            const selectedWatercraft = Watercrafts.find((wc) => wc.hin === watercraft.value.hin);

            const details={
                data: getWatercraftName(watercraft),
                pageAction,
                fixedId:selectedWatercraft.fixedID,
                additionalData : selectedWatercraft 
            }
            
            MLNavigationStepsTrackingService.completeCurrentStep(details, authHeader , currentStep);
            jumpToSelectedStepByStepID('endorsementAdditionalChanges');        
        });
    };

    const onNext = () => {
        const isValid = watercraft.aspects.valid && watercraft.aspects.subtreeValid;
        if (!isValid) {
            setShowValidationErrors(true);
            setErrorTimestamp(errorTimestamp+1);
        }
        if (!isValid) return;

        const lbl = MLEndorsementUtil.stepLabel(currentStep);
        MLEndorsementUtil.setEndorsementDescription(lbl, submissionVM, translator);
        if (pageAction === 'add') {
            addNewWatercraftAlert(watercraft);
        }

        setInSync(true);
        MLTagManagerService.sendFormStepPolicyChangeStep(`${pageAction} Watercraft`);
        saveEndorsement();
    };

    const resolvers = {
        resolveComponentMap: {
            watercraftPopupComponent: WatercraftPopupComponent
        },
        resolveCallbackMap: {
            onNext
        },
        resolveClassNameMap: styles
    };

    const overrides = {
        contentContainer: {
            className: isGPA ? 'page-content-gpa' : ''
        },
        gpaHeaderTitle: {
            visible: !isAMP,
            content: translator(messages.watercraft)
        },
        addEditHeaderTitle: {
            visible: isAMP && (pageAction === 'add' || pageAction === 'edit'),
            content: pageAction === 'add' ? translator(messages.addWatercraft) : translator(messages.editWatercraft)
        },
        addEditHeaderMsg: {
            visible: isAMP
        },
        watercraftAddEditComponent: {
            pupModel,
            onValidate,
            showValidationErrors,
            watercraftVM: watercraft,
            getUnderlyingPolicies,
            syncExposure: syncWatercraftExposure,
            createUnderlyingPolicy: createNewULPolicy,
            showProgressDialog,
            showActionRow: false,
            onModelChange: () => refreshData()
        },
        nextDescription: {
            content: getNextStepText()
        },
        stickyContinueSection: {
            visible: isGPA,
            defaultContinueSectionId: 'footer',
            nextDescription: getNextStepText(),
            onNext
        }
    };

    return (
        <WizardPage
            showCancel={false}
            showPrevious={false}
            showNext={false}
            policyChangePageHeaderML={isAMP ? null : messages.makeChanges}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                overrideProps={overrides}
                model={submissionVM}
                onModelChange={updateWizardData}
                componentMap={resolvers.resolveComponentMap}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                onValidationChange={onValidate}
                showErrors={showValidationErrors}
            />
            <ScrollToError counter={errorTimestamp} />
        </WizardPage>
    );
};

WatercraftPagePolChange.propTypes = wizardProps;
export default WatercraftPagePolChange;